import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, Mail } from "lucide-react";

interface EmailConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  onConfirmed: (code: string) => void;
}

export function EmailConfirmationModal({ 
  open, 
  onOpenChange, 
  email, 
  onConfirmed 
}: EmailConfirmationModalProps) {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const sendConfirmationCode = async () => {
    if (!email) return;
    
    setIsSending(true);
    try {
      await apiRequest("POST", "/api/auth/send-confirmation", { email });
      toast({
        title: "Confirmation code sent",
        description: "Check your email for the confirmation code.",
      });
    } catch (error) {
      console.error("Failed to send confirmation code:", error);
      toast({
        title: "Error",
        description: "Failed to send confirmation code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const verifyCode = async () => {
    if (!code || code.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter a 6-digit confirmation code.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      onConfirmed(code);
    } catch (error) {
      console.error("Failed to verify confirmation code:", error);
      toast({
        title: "Invalid code",
        description: "The confirmation code is invalid or expired. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (value: string) => {
    // Only allow digits and limit to 6 characters
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setCode(numericValue);
  };

  // Auto-send confirmation code when modal opens
  React.useEffect(() => {
    if (open && email) {
      sendConfirmationCode();
      setCode(""); // Reset code when modal opens
    }
  }, [open, email]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Confirm Your Email
          </DialogTitle>
          <DialogDescription>
            We've sent a 6-digit confirmation code to <strong>{email}</strong>. 
            Enter the code below to verify your email address.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="confirmation-code">Confirmation Code</Label>
            <Input
              id="confirmation-code"
              type="text"
              placeholder="000000"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              className="text-center text-lg font-mono tracking-widest"
              maxLength={6}
            />
            <p className="text-sm text-muted-foreground">
              Code expires in 4 minutes
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={verifyCode} 
              disabled={isVerifying || code.length !== 6}
              className="flex-1"
            >
              {isVerifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Verify Code
            </Button>
            
            <Button 
              variant="outline" 
              onClick={sendConfirmationCode}
              disabled={isSending}
            >
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Resend
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}